<template>
  <v-card>
    <v-toolbar color="primary" dark tabs>
      <v-toolbar-title>
        {{
          selected._id
            ? $t('titles.global.updateModel', { field: $tc('models.training') })
            : $t('titles.global.newModel', { field: $tc('models.training') })
        }}
      </v-toolbar-title>
      <v-spacer />
      <v-btn
        v-if="selected._id"
        icon
        dark
        @click="$store.dispatch('trainings/fetchOne', selected._id)"
      >
        <v-icon>refresh</v-icon>
      </v-btn>
      <v-btn dark icon @click="cancel">
        <v-icon>close</v-icon>
      </v-btn>
      <v-tabs
        slot="extension"
        v-model="tab"
        color="primary"
        slider-color="white"
        centered
      >
        <v-tab>{{ $tc('models.training') }}</v-tab>
        <v-tab :disabled="!selected._id">
          {{ $tc('models.event', 2) }}
        </v-tab>
      </v-tabs>
    </v-toolbar>
    <v-card-text>
      <v-progress-linear
        :active="loadingItem"
        height="3"
        :indeterminate="true"
        class="my-1"
      />
      <form-loader v-if="loadingItem" />
      <template v-else>
        <v-tabs-items v-model="tab">
          <v-tab-item ripple>
            <tab-information ref="tabInformation" :training.sync="training" />
          </v-tab-item>
          <v-tab-item ripple>
            <tab-events
              :training-id="selected._id"
              :home-team-id="training.teamId"
              :location="training.location"
              :video="video"
            />
          </v-tab-item>
        </v-tabs-items>
      </template>
    </v-card-text>
    <v-divider />
    <v-card-actions>
      <span style="color: red">*</span>
      <span>{{ $t('action.messages.fieldRequired') }}</span>
    </v-card-actions>
    <v-card-actions>
      <v-spacer />
      <template v-if="tab != 3">
        <template v-if="!training._id">
          <meta-button
            v-if="!loading"
            :disabled="loading"
            type="reset"
            @click="clear"
          />
          <meta-button type="create" :loading="loading" @click="submit()" />
        </template>
        <template v-else>
          <button-confirm
            v-if="canReset && can('edit', 'training', training)"
            color="error"
            :text="$t('action.buttons.resetTraining')"
            :text-info="$t('titles.matches.resetTrainingInfo')"
            :disabled="loading"
            @submit="resetTraining(training._id)"
          />
          <meta-button type="update" :loading="loading" @click="submit()" />
        </template>
      </template>
      <meta-button type="cancel" @click="cancel" />
    </v-card-actions>
  </v-card>
</template>

<script>
import moment from 'moment'
import { mapActions, mapState } from 'vuex'

import TabInformation from './form/TabInformation'
import TabEvents from '@/components/apps/matches/TabEvents'
import ButtonConfirm from '@/components/widgets/button/ButtonConfirm'
import { formatDatePicker } from '@/helpers/dateHelper'
import { parseObject } from '@/helpers/utils'

export default {
  name: 'TrainingForm',
  components: {
    TabInformation,
    TabEvents,
    ButtonConfirm,
  },
  props: {
    id: {
      type: String,
      required: false,
    },
    action: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      training: {},
      tab: 0,
      loadingItem: false,
    }
  },
  watch: {
    selected: {
      handler: async function (val) {
        if (val) {
          await this.setObject(val)
        }
      },
      immediate: true,
      deep: true,
    },
  },
  mounted() {
    this.reset()
    if (this.action == 'edit' && this.id) {
      this.nextTick(() => {
        this.loadingItem = true
        this.fetchOne(this.id)
          .then(() => {
            this.setTab()
          })
          .catch(() => {
            this.cancel()
          })
          .finally(() => {
            this.loadingItem = false
          })
      })
    }
  },
  computed: {
    ...mapState({
      selected: (state) => state.trainings.selected,
      loading: (state) => state.trainings.loading,
      video: (state) => state.trainings.video,
    }),
    canReset() {
      let canReset = true
      if (!this.training || typeof this.training !== 'object') {
        canReset = false
      } else if (this.training.started && !this.training.ended) {
        canReset = false
      } else if (
        this.training.streamingStarted &&
        !this.training.streamingEnded
      ) {
        canReset = false
      }
      return canReset && (this.isAdmin || this.training.expiredReset === false)
    },
  },
  methods: {
    ...mapActions({
      setCreate: 'trainings/create',
      setUpdate: 'trainings/update',
      fetchOne: 'trainings/fetchOne',
      reset: 'trainings/reset',
      resetTraining: 'trainings/resetTraining',
      fetchUserSelect: 'users/fetchSelectList',
    }),
    submit() {
      this.$nextTick(() => {
        this.$refs.tabInformation.validate().then((valid) => {
          if (!valid) return
          let payload = Object.assign({}, this.training)
          if (payload.autoStreaming) {
            payload.dateAutoStreaming = moment(payload.date)

            let [hour, minute] = payload.timeAutoStreaming.split(':')
            payload.dateAutoStreaming.set({ hour, minute, second: 0 })
            // delete payload.broadcastId
            // delete payload.timeAutoStreaming
          }
          if (!payload._id) {
            this.setCreate(payload).then((result) => {
              this.$store.dispatch('trainings/fetchOne', result._id)
            })
          } else {
            this.setUpdate(payload).then((result) => {
              this.$store.dispatch('trainings/fetchOne', result._id)
            })
          }
        })
      })
    },
    async setObject(training) {
      this.training = parseObject(training)
      this.training.date = formatDatePicker(training.date)

      // if (training.dateAutoStreaming) {
      //   this.training.timeAutoStreaming = moment(
      //     training.dateAutoStreaming
      //   ).format('HH:mm')
      // }
      let params = {
        _id: [],
      }
      if (this.training.teamId) {
        params._id.push(this.training.teamId)
        let teams = await this.$store.dispatch('teams/fetchSelectList', params)

        if (teams.length == 1) {
          this.training.entityId = teams[0].entityId
        } else {
          this.training.entityId = null
        }
      }
    },
    setTab() {
      switch (this.$route.params.tab) {
        case 'events':
          this.tab = 1
          break
        default:
          this.tab = 0
          break
      }
    },
    clear() {
      this.nextTick(() => {
        this.reset()
        this.tab = 0
        if (this.$refs.tabInformation) this.$refs.tabInformation.clear()
      })
    },
    cancel() {
      this.clear()
      let name = this.from && this.from.name ? this.from.name : 'trainings'
      this.$router.push({ name, params: { resetFilters: false } })
    },
  },
}
</script>
